import {useState} from 'react';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {Button, IconButton, Tooltip, Typography} from '@mui/material';
import {MRT_ColumnDef} from 'material-react-table';

import {useGetVoucherConfigs, VoucherConfig} from '@entities/voucher';
import {
  CardBox,
  CurrencyValue,
  MrTable,
  TableRowActionsContainer,
} from '@shared/components';
import {entityTableCommonProps} from '@shared/constants';
import {useUrlState} from '@shared/hooks';
import {PaginationState} from '@shared/types';
import {Header} from '@widgets/common';
import {ConfigDialog} from './ConfigDialog';

const columns: MRT_ColumnDef<VoucherConfig>[] = [
  {accessorKey: 'title', header: 'Name', enableSorting: false},
  {
    id: 'amounts',
    header: 'Amounts',
    enableSorting: false,
    Cell: ({row}) => (
      <>
        {row.original.amounts.map(({currency, amount}) => (
          <div key={currency}>
            <CurrencyValue
              value={amount}
              prefix=""
              suffix={` ${currency}`}
              decimalScale={4}
              fixedDecimalScale={false}
            />
          </div>
        ))}
      </>
    ),
  },
  {accessorKey: 'ttlDays', header: 'Lifetime (days)', enableSorting: false},
];

export const VoucherConfigsPage = () => {
  const {urlState, setUrl} = useUrlState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const {data, isPending, isFetching, error} = useGetVoucherConfigs({
    page: urlState.pageIndex + 1,
    pageSize: urlState.pageSize,
  });
  const [dialog, setDialog] = useState<{configId?: string} | null>(null);

  return (
    <>
      <Header title="Vouchers" mb={1} />
      <CardBox>
        <MrTable
          {...entityTableCommonProps}
          columns={columns}
          data={data?.items ?? []}
          error={error}
          getRowId={row => row.id}
          enablePagination
          manualPagination
          onPaginationChange={setUrl}
          rowCount={data?.total ?? 0}
          state={{
            isLoading: isPending,
            showProgressBars: isFetching && !isPending,
            pagination: urlState,
          }}
          renderTopToolbarCustomActions={() => (
            <Button onClick={() => setDialog({})}>Add voucher</Button>
          )}
          renderEmptyRowsFallback={() => (
            <Typography textAlign="center" p={3}>
              {error
                ? 'Could not load vouchers.'
                : 'No vouchers. Add a voucher to get started.'}
            </Typography>
          )}
          renderRowActions={({row}) => (
            <TableRowActionsContainer>
              <Tooltip title="Edit voucher">
                <IconButton
                  aria-label={`Edit ${row.original.title} voucher`}
                  onClick={() => setDialog({configId: row.original.id})}
                >
                  <EditOutlinedIcon />
                </IconButton>
              </Tooltip>
            </TableRowActionsContainer>
          )}
        />
      </CardBox>
      {dialog && (
        <ConfigDialog
          configId={dialog.configId}
          onClose={() => setDialog(null)}
        />
      )}
    </>
  );
};
